import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faBarChart, faSave } from "@fortawesome/free-solid-svg-icons";
import { Line } from "react-chartjs-2";
import useFetch from "../../hooks/useFetch";

const ReelShortsHome = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const user = authState?.user || {};
  const userId = user._id;
  const [reelUrl, setReelUrl] = useState("");
  const [userPoints, setUserPoints] = useState(0);
  const [dailyPoints, setDailyPoints] = useState(0);
  const [streakDays, setStreakDays] = useState(0);
  const [activities, setActivities] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [saveDisabled, setSaveDisabled] = useState(false);

  const [fetchData, { data, refetch }] = useFetch();
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [prizeMessage, setPrizeMessage] = useState("");
  
  // Load activities
  useEffect(() => {
    if (data) setActivities(data.history || []);
  }, [data]);

  useEffect(() => {
    if (userId) {
      fetchData({
        url: "/reelshorts/history",
        method: "get",
        headers: { Authorization: authState.token },
      });
    }
  }, [userId]);

  const handleSave = async () => {
    if (!reelUrl.trim()) return;

    setSaveDisabled(true); // disable button while saving

    const platform = (() => {
      if (reelUrl.includes("instagram.com")) return "instagram";
      if (reelUrl.includes("youtube.com") || reelUrl.includes("youtu.be")) return "youtube";
      if (reelUrl.includes("tiktok.com")) return "tiktok";
      return "other";
    })();

    try {
      const result = await fetchData({
        url: "/reelshorts",
        method: "post",
        data: { user: userId, reelUrl, platform },
        headers: { Authorization: authState.token },
      });

      if (result?.success) {
        setUserPoints(result.userPoints || userPoints + 1);
        setDailyPoints(result.dailyPoints || dailyPoints + 1);
        setStreakDays(result.streakDays || streakDays);
        refetch();
        setReelUrl("");
      }
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaveDisabled(false);
    }
  };

  // Validation function
    const isValidReelUrl = (url) => {
    if (!url) return false;
    const patterns = [
        /^(https?:\/\/)?(www\.)?instagram\.com\/reel\/[A-Za-z0-9_-]+/i,
        /^(https?:\/\/)?(www\.)?youtube\.com\/shorts\/[A-Za-z0-9_-]+/i,
        /^(https?:\/\/)?(youtu\.be\/[A-Za-z0-9_-]+)/i,
        /^(https?:\/\/)?(www\.)?tiktok\.com\/@[A-Za-z0-9._-]+\/video\/[0-9]+/i
    ];
    return patterns.some((pattern) => pattern.test(url));
    };

    // Input change handler
    const handleInputChange = (e) => {
    const value = e.target.value.trim();
    setReelUrl(value);
    setSaveDisabled(!isValidReelUrl(value)); // disable save unless URL is valid
    };

  const filteredActivities = activities.filter((act) => {
    const matchesSearch = act.reelUrl.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlatform = filterPlatform === "all" ? true : act.platform.toLowerCase() === filterPlatform;
    return matchesSearch && matchesPlatform;
  });

  const chartData = {
    labels: filteredActivities.map((a) => new Date(a.watchedAt).toLocaleDateString()),
    datasets: [
      {
        label: "Daily Points",
        data: filteredActivities.map((_, idx) => idx + 1),
        fill: false,
        borderColor: "purple",
        tension: 0.3,
      },
    ],
  };

  const handlePrizeClick = () => {
    // Randomly decide win or lose
    const won = Math.random() < 0.5; // 50% chance
    setPrizeMessage(
        won ? "🎉 You Won a Prize! Click here to claim." : "😔 Sorry! Try Your Luck Next Time."
    );
    setShowPrizeModal(true);
    };

  return (
    <div className="min-h-screen bg-black text-purple-400 p-3 lg:p-8">
      {/* Header */}
      <div className="mb-1 lg:mb-2">
        <h1 className="text-lg lg:text-xl text-yellow-200 font-bold">🎬 ReelDekho 🏆 PrizeJeeto</h1>
        <p className="mt-1 textwhite text-xs lg:text-sm text-center">
          Watch reels/shorts, save, and earn points!
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 lg:gap-4 mb-2 text-sm lg:text-base">
        <div className="bg-gray-900 p-3 lg:p-4 rounded-lg shadow">
          <h2 className="font-semibold text-sm lg:text-lg">Total Points</h2>
          <p className="text-lg lg:text-2xl">{userPoints}</p>
        </div>
        <div className="bg-gray-900 p-3 lg:p-4 rounded-lg shadow">
          <h2 className="font-semibold text-sm lg:text-lg">Daily Points</h2>
          <p className="text-lg lg:text-2xl">{dailyPoints}</p>
        </div>
        <div className="bg-gray-900 p-3 lg:p-4 rounded-lg shadow">
          <h2 className="font-semibold text-sm lg:text-lg">Streak Days</h2>
          <p className="text-lg lg:text-2xl">{streakDays}</p>
        </div>
        <div className="bg-purple-500 p-3 lg:p-4 rounded-lg shadow cursor-pointer"
            onClick={handlePrizeClick}>
            <h2 className="font-semibold text-sm lg:text-lg">Prize Status</h2>
            <p className="text-lg lg:text-2xl">Click Here To View</p>
        </div>        
      </div>

      {showPrizeModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50">
            <div className="bg-gray-900 p-4 lg:p-6 rounded-lg shadow-lg text-center max-w-sm w-full">
            <p className="text-purple-400 mb-4">{prizeMessage}</p>
            {prizeMessage.includes("Click here") && (
                <button className="bg-purple-700 hover:bg-purple-600 p-2 rounded-lg mb-2">
                Claim Prize
                </button>
            )}
            <button
                onClick={() => setShowPrizeModal(false)}
                className="bg-gray-700 hover:bg-gray-600 p-2 rounded-lg"
            >
                Close
            </button>
            </div>
        </div>
        )}

      {/* Save Reel */}
      <div className="mb-4 lg:mb-6">
        <h2 className="font-semibold text-sm lg:text-xl mb-2">Save a Reel/Short</h2>
        <div className="flex flex-col lg:flex-row gap-2">
          <input
            type="text"
            placeholder="Paste Reel URL..."
            value={reelUrl}
            onChange={handleInputChange}
            className="flex-1 p-2 text-sm lg:text-base rounded-lg bg-gray-900 border border-purple-700 focus:outline-none"
          />
          <button
            onClick={handleSave}
            disabled={saveDisabled}
            className={`bg-purple-700 hover:bg-purple-600 p-2 lg:p-2 rounded-lg flex items-center justify-center w-full lg:w-28 text-sm lg:text-base ${
                saveDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            >
            <FontAwesomeIcon icon={faSave} className="mr-1" /> Save
          </button>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:gap-3 mb-4 lg:mb-6 text-sm lg:text-base">
        <div className="flex items-center bg-gray-900 p-2 rounded-lg mb-2 lg:mb-0">
          <FontAwesomeIcon icon={faSearch} className="mr-2" />
          <input
            type="text"
            placeholder="Search reels..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-gray-900 focus:outline-none text-sm lg:text-base"
          />
        </div>
        <select
          value={filterPlatform}
          onChange={(e) => setFilterPlatform(e.target.value)}
          className="p-2 rounded-lg bg-gray-900 border border-purple-700 focus:outline-none text-sm lg:text-base"
        >
          <option value="all">All Platforms</option>
          <option value="instagram">Instagram</option>
          <option value="youtube">YouTube</option>
          <option value="tiktok">TikTok</option>
        </select>
      </div>

      {/* Activities Table */}
      <div className="overflow-x-auto mb-4 lg:mb-6 text-sm lg:text-base">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-purple-700">
              <th className="p-2">Platform</th>
              <th className="p-2">Reel URL</th>
              <th className="p-2">Saved At</th>
              <th className="p-2">Points</th>
            </tr>
          </thead>
          <tbody>
            {filteredActivities.map((act, idx) => (
              <tr key={idx} className="border-b border-gray-700 hover:bg-gray-800">
                <td className="p-1 lg:p-2">{act.platform}</td>
                <td className="p-1 lg:p-2 break-all">{act.reelUrl}</td>
                <td className="p-1 lg:p-2">{new Date(act.watchedAt).toLocaleString()}</td>
                <td className="p-1 lg:p-2">{act.pointsEarned}</td>
              </tr>
            ))}
            {filteredActivities.length === 0 && (
              <tr>
                <td colSpan={4} className="p-2 text-center text-purple-500">
                  No reels saved yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Chart */}
      <div className="bg-gray-900 p-3 lg:p-4 rounded-lg shadow">
        <h2 className="font-semibold text-sm lg:text-xl mb-2">Reel Watching Trends</h2>
        <Line
          data={chartData}
          options={{
            responsive: true,
            plugins: { legend: { labels: { color: "purple" } } },
            scales: {
              x: { ticks: { color: "purple" } },
              y: { ticks: { color: "purple" } },
            },
          }}
        />
      </div>
    </div>
  );
};

export default ReelShortsHome;
